import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import ClientDetails from "@/pages/ClientDetails";
import UserDetails from "@/pages/UserDetails";
import { formatPhone } from "@/lib/utils";
import { format } from "date-fns";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DateField } from "@/components/ui/date-field";
import { startOfDay, endOfDay, subDays } from "date-fns";
import { MoneyInput } from "@/components/ui/money-input";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";
import { Loader2, Plus, FileText, X, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SaleItemsCards, type SaleItemsCardsHandle } from "./SaleItemsCards";
import { PaymentsList } from "./PaymentsList";
import { UserModal } from "@/components/users/UserModal";
import { QuickAddClientModal } from "./QuickAddClientModal";
import { PaymentOnboarding } from "./PaymentOnboarding";
import { useTenant } from "@/contexts/TenantContext";
import { PrintPreviewModal } from "./PrintPreviewModal";

const saleSchema = z.object({
  client_id: z.string().uuid({ message: "Selecione um cliente" }),
  vendedor_id: z.string().uuid({ message: "Selecione um vendedor" }),
  desconto: z.number().min(0).default(0),
  data_entrega: z.string().optional(),
  descricao: z.string().optional(),
});

type SaleFormData = z.infer<typeof saleSchema>;

type SaleItem = {
  product_id: string;
  quantidade: number;
  preco_unitario: number;
  desconto: number;
  total: number;
  form_values?: Record<string, any>;
};

type Payment = {
  data: string;
  bank_account_id: string;
  payment_method_id: string;
  valor: number;
  net_amount?: number;
  fee_amount?: number;
  descricao?: string;
  status: "confirmado" | "pendente";
  data_prevista?: string;
  created_at?: string;
  confirmed_at?: string;
};

interface NewSaleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction?: any;
  readOnly?: boolean;
}

export function NewSaleModal({ open, onOpenChange, transaction, readOnly = false }: NewSaleModalProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<SaleItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  // Quick add do cliente agora é controlado pelo próprio SearchableSelect via renderAddModal
  const [clientSelectOpen, setClientSelectOpen] = useState(false);
  const [sellerSelectOpen, setSellerSelectOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [productSelectOpen, setProductSelectOpen] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const queryClient = useQueryClient();
  const { tenantId, tenantName } = useTenant();
  const saleItemsRef = useRef<SaleItemsCardsHandle | null>(null);
  const [clientProfile, setClientProfile] = useState<{ id: string; name: string } | null>(null);
  const [userProfile, setUserProfile] = useState<{ id: string; name: string } | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);
  

  // Buscar configurações de liquidação
  const { data: settlementConfigs = [] } = useQuery({
    queryKey: ["settlement-configs", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("payment_settlement_configs")
        .select("*")
        .eq("tenant_id", tenantId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  // Obter usuário logado
  const { data: currentUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Verificar se deve exibir onboarding ao abrir o modal
  const { data: bankAccounts = [] } = useQuery({
    queryKey: ["bank_accounts", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id")
        .eq("tenant_id", tenantId)
        .eq("status", "ativo");

      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  // Controlar abertura do select de cliente
  useEffect(() => {
    if (open && !transaction) {
      setClientSelectOpen(true);
    } else if (!open) {
      setClientSelectOpen(false);
      setShowOnboarding(false);
    }
  }, [open, transaction]);

  const form = useForm<SaleFormData>({
    resolver: zodResolver(saleSchema),
    defaultValues: {
      client_id: "",
      vendedor_id: "",
      desconto: 0,
      data_entrega: new Date().toISOString().split("T")[0],
      descricao: "",
    },
  });

  // valor observado do cliente para re-renderizar ao mudar
  const clientId = form.watch("client_id");
  const canProceed = !!clientId && (items.length > 0 && items.some((it) => it.product_id));

  // Mostrar onboarding SOMENTE quando não houver contas bancárias cadastradas
  useEffect(() => {
    if (!open || transaction) return;

    const hasClient = !!clientId;
    const hasProduct = items.length > 0 && items.some((item) => item.product_id);
    const noAccounts = (bankAccounts?.length || 0) === 0;
    // Wizard deve aparecer apenas quando não existem contas bancárias
    const shouldShow = hasClient && hasProduct && noAccounts;

    setShowOnboarding(shouldShow);
  }, [open, clientId, items, bankAccounts, transaction]);

  // Pré-selecionar usuário logado como vendedor sempre que abrir para NOVA venda
  useEffect(() => {
    if (!open || transaction) return;
    if (currentUser?.id) {
      form.setValue("vendedor_id", currentUser.id, { shouldDirty: false });
    }
  }, [open, transaction, currentUser, form]);

  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase.from("clients").select("*").order("nome");
      if (error) throw error;
      return data;
    },
  });

  const { data: sellers } = useQuery({
    queryKey: ["sellers", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("tenant_id", tenantId)
        .order("nome_completo");
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  // Buscar dados completos do tenant para impressão
  const { data: tenantData } = useQuery({
    queryKey: ["tenant-data", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await supabase
        .from("tenants")
        .select("name, email")
        .eq("id", tenantId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  // Carregar dados da transação para edição
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    if (transaction && open) {
      console.log("🔍 Carregando transação para edição:", transaction);
      
      form.reset({
        client_id: transaction.client_id || "",
        vendedor_id: transaction.vendedor_id || "",
        desconto: Number(transaction.desconto) || 0,
        data_entrega: transaction.data_entrega || "",
        descricao: transaction.descricao || "",
      });

      // Carregar itens da transação
      const loadItems = async () => {
        console.log("📦 Buscando itens da transação:", transaction.id);
        
        const { data: transactionItems, error } = await supabase
          .from("transaction_items")
          .select("*")
          .eq("transaction_id", transaction.id);

        console.log("📦 Itens encontrados:", transactionItems, "Erro:", error);

        if (transactionItems && transactionItems.length > 0) {
          // Buscar valores do formulário para cada item
          const itemsWithFormValues = await Promise.all(
            transactionItems.map(async (item) => {
              // Buscar submissões do formulário relacionadas a este transaction_item
              const { data: submissions } = await supabase
                .from("form_submissions")
                .select("id, form_id")
                .eq("entity_type", "transaction")
                .eq("entity_id", transaction.id)
                .eq("custom_identifier", item.id);

              if (!submissions || submissions.length === 0) {
                return {
                  product_id: item.product_id || "",
                  quantidade: item.quantidade,
                  preco_unitario: Number(item.preco_unitario),
                  desconto: 0,
                  total: Number(item.total),
                  form_values: undefined,
                };
              }

              // Buscar valores da submissão mais recente
              const latestSubmission = submissions[0];
              const { data: submissionValues } = await supabase
                .from("form_submission_values")
                .select(`
                  valor,
                  valor_json,
                  field_id,
                  form_fields(id, slug)
                `)
                .eq("submission_id", latestSubmission.id);

              // Construir objeto form_values
              const formValues: Record<string, any> = {};
              if (submissionValues) {
                submissionValues.forEach((sv: any) => {
                  const field = sv.form_fields;
                  if (field && field.slug) {
                    // Usar valor_json se disponível, senão valor
                    let v = sv.valor_json !== null ? sv.valor_json : sv.valor;
                    if (typeof v === "string") {
                      try {
                        const parsed = JSON.parse(v);
                        if (Array.isArray(parsed)) v = parsed;
                      } catch {}
                    }
                    formValues[field.slug] = v;
                  }
                });
              }

              return {
                product_id: item.product_id || "",
                quantidade: item.quantidade,
                preco_unitario: Number(item.preco_unitario),
                desconto: 0,
                total: Number(item.total),
                form_values: Object.keys(formValues).length > 0 ? formValues : undefined,
              };
            })
          );

          console.log("✅ Itens carregados com valores do formulário:", itemsWithFormValues);
          setItems(itemsWithFormValues);
        } else {
          console.log("⚠️ Nenhum item encontrado para esta transação");
          setItems([]);
        }
      };

      loadItems();

      // Carregar pagamentos da nova tabela
      const loadPays = async () => {
        const { data: loadedPays } = await supabase
          .from("transaction_payments")
          .select("bank_account_id, payment_method_id, valor, net_amount, fee_amount, status, data, descricao, data_prevista, created_at, confirmed_at")
          .eq("transaction_id", transaction.id)
          .order("created_at", { ascending: true });
        setPayments(
          (loadedPays || []).map((p: any) => ({
            bank_account_id: p.bank_account_id,
            payment_method_id: p.payment_method_id,
            valor: Number(p.valor) || 0,
            net_amount: p.net_amount !== null && p.net_amount !== undefined ? Number(p.net_amount) : undefined,
            fee_amount: p.fee_amount !== null && p.fee_amount !== undefined ? Number(p.fee_amount) : undefined,
            data: p.data || new Date().toISOString().split("T")[0],
            status: p.status,
            descricao: p.descricao || "",
            data_prevista: p.data_prevista || "",
            ...(p.created_at ? { created_at: p.created_at } : {}),
            ...(p.confirmed_at ? { confirmed_at: p.confirmed_at } : {}),
          })) as any
        );
      };
      loadPays();
    } else if (!open) {
      // Reset quando fechar
      form.reset({
        client_id: "",
        vendedor_id: "",
        desconto: 0,
        data_entrega: today,
        descricao: "",
      });
      setItems([]);
      setPayments([]);
    } else if (open && !transaction) {
      form.setValue("data_entrega", today, { shouldDirty: false });
    }
  }, [transaction, open, form]);

  const createSaleMutation = useMutation({
    mutationFn: async (data: SaleFormData) => {
      const valorTotalItens = items.reduce((sum, item) => sum + item.total, 0);
      const valorLiquido = valorTotalItens - data.desconto;
      const valorPago = payments.filter(p => p.status === "confirmado").reduce((sum, p) => sum + p.valor, 0);
      const valorPendente = valorLiquido - valorPago;

      let status: "pendente" | "paga" = "pendente";
      if (valorPago >= valorLiquido) status = "paga";

      // Buscar sessão aberta do tenant para vincular à transação (fallback: null)
      let openSessionId: string | null = null;
      if (tenantId) {
        const { data: openSession } = await supabase
          .from("cash_register_sessions")
          .select("id")
          .eq("tenant_id", tenantId)
          .eq("status", "open")
          .order("opened_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        openSessionId = openSession?.id ?? null;
      }

      const transactionData: any = {
        tipo: "entrada" as const,
        client_id: data.client_id,
        vendedor_id: data.vendedor_id,
        valor_total: valorTotalItens,
        valor_liquido: valorLiquido,
        desconto: data.desconto,
        status,
        data_entrega: data.data_entrega || null,
        descricao: data.descricao || null,
        tenant_id: tenantId,
        cash_register_session_id: openSessionId,
      };

      let transactionResult;

      if (transaction?.id) {
        // Atualizar transação existente
        const { data: updatedTransaction, error: transactionError } = await supabase
          .from("transactions")
          .update(transactionData)
          .eq("id", transaction.id)
          .select()
          .single();

        if (transactionError) throw transactionError;
        transactionResult = updatedTransaction;

        // Excluir submissões de formulário relacionadas aos itens antigos
        const { data: oldItems } = await supabase
          .from("transaction_items")
          .select("id")
          .eq("transaction_id", transaction.id);

        if (oldItems && oldItems.length > 0) {
          const oldItemIds = oldItems.map(item => item.id);
          const { data: oldSubmissions } = await supabase
            .from("form_submissions")
            .select("id")
            .eq("entity_type", "transaction")
            .eq("entity_id", transaction.id)
            .in("custom_identifier", oldItemIds);

          if (oldSubmissions && oldSubmissions.length > 0) {
            const submissionIds = oldSubmissions.map(s => s.id);
            // Excluir valores primeiro (devido à foreign key)
            await supabase
              .from("form_submission_values")
              .delete()
              .in("submission_id", submissionIds);
            // Excluir submissões
            await supabase
              .from("form_submissions")
              .delete()
              .in("id", submissionIds);
          }
        }

        // Excluir itens antigos
        await supabase.from("transaction_items").delete().eq("transaction_id", transaction.id);
      } else {
        // Criar nova transação
        const { data: newTransaction, error: transactionError } = await supabase
          .from("transactions")
          .insert([transactionData])
          .select()
          .single();

        if (transactionError) throw transactionError;
        transactionResult = newTransaction;
      }

      // Inserir novos itens
      const itemsToInsert = items.map((item) => ({
        transaction_id: transactionResult.id,
        product_id: item.product_id,
        quantidade: item.quantidade,
        preco_unitario: item.preco_unitario,
        total: item.total,
      }));

      const { data: insertedItems, error: itemsError } = await supabase
        .from("transaction_items")
        .insert(itemsToInsert)
        .select();

      if (itemsError) throw itemsError;

      // Salvar valores do formulário para cada item que tiver form_values
      if (insertedItems && insertedItems.length > 0) {
        // Buscar form_id de cada produto
        const productIds = items.map(item => item.product_id).filter(Boolean);
        const { data: products } = await supabase
          .from("products")
          .select("id, form_id")
          .in("id", productIds);

        const productFormMap = new Map(products?.map(p => [p.id, p.form_id]) || []);

        // Buscar informações do usuário atual
        const { data: { user } } = await supabase.auth.getUser();
        const { data: profile } = user ? await supabase
          .from("profiles")
          .select("id, nome_completo, email")
          .eq("id", user.id)
          .single() : { data: null };

        // Para cada item inserido, salvar form_values se existir
        for (let i = 0; i < insertedItems.length; i++) {
          const insertedItem = insertedItems[i];
          const originalItem = items[i];
          
          if (originalItem.form_values && Object.keys(originalItem.form_values).length > 0) {
            const formId = productFormMap.get(originalItem.product_id);
            if (!formId) continue;

            // Buscar campos do formulário para mapear valores
            const { data: formFields } = await supabase
              .from("form_fields")
              .select("id, slug")
              .eq("form_id", formId);

            if (!formFields || formFields.length === 0) continue;

            // Criar submissão do formulário
            const { data: submission, error: submissionError } = await supabase
              .from("form_submissions")
              .insert({
                form_id: formId,
                entity_type: "transaction",
                entity_id: transactionResult.id,
                custom_identifier: insertedItem.id, // Usar transaction_item_id como identificador
                submitted_by: profile?.id || null,
                submitted_by_email: profile?.email || null,
                submitted_by_name: profile?.nome_completo || null,
                status: "aprovado",
                tenant_id: tenantId,
              })
              .select()
              .single();

            if (submissionError) {
              console.error("Erro ao criar submissão do formulário:", submissionError);
              continue;
            }

            // Salvar valores dos campos
            const valuesToInsert = formFields
              .filter(field => originalItem.form_values?.[field.slug] !== undefined)
              .map(field => {
                const value = originalItem.form_values[field.slug];
                const isArray = Array.isArray(value);
                const isObject = typeof value === "object" && value !== null && !isArray;

                return {
                  submission_id: submission.id,
                  field_id: field.id,
                  valor: (isArray || isObject) ? JSON.stringify(value) : String(value || ""),
                  valor_json: (isArray || isObject) ? value : null,
                  tenant_id: tenantId,
                };
              });

            if (valuesToInsert.length > 0) {
              const { error: valuesError } = await supabase
                .from("form_submission_values")
                .insert(valuesToInsert);

              if (valuesError) {
                console.error("Erro ao salvar valores do formulário:", valuesError);
              }
            }
          }
        }
      }

      // Persistir pagamentos na tabela transaction_payments
      // Limpar existentes e inserir os atuais
      await supabase.from("transaction_payments").delete().eq("transaction_id", transactionResult.id);
      if ((payments || []).length > 0) {
        // Criar mapa de configs por (conta, método)
        const configMap = new Map<string, any>();
        settlementConfigs.forEach(config => {
          const key = `${config.bank_account_id}-${config.payment_method_id}`;
          configMap.set(key, config);
        });

        const paymentsToInsert = payments.map((p: any) => {
          // Buscar config de liquidação
          const configKey = `${p.bank_account_id}-${p.payment_method_id}`;
          const settlementConfig = configMap.get(configKey);

          let netAmount = typeof p.net_amount === "number" ? Number(p.net_amount.toFixed(2)) : undefined;
          let feeAmount = typeof p.fee_amount === "number" ? Number(p.fee_amount.toFixed(2)) : undefined;
          let dataPrevista = p.data_prevista || p.data || new Date().toISOString().slice(0, 10);

          if (typeof netAmount !== "number") {
            netAmount = Number(p.valor);
          }
          if (typeof feeAmount !== "number") {
            feeAmount = Number((p.valor - netAmount).toFixed(2));
          }

          if (settlementConfig && p.status === 'confirmado' && typeof p.net_amount !== "number") {
            // Aplicar taxa padrão apenas quando não houver valor líquido informado manualmente
            const configFee = Number((p.valor * (settlementConfig.fee_percent / 100)).toFixed(2));
            feeAmount = configFee;
            netAmount = Number((p.valor - configFee).toFixed(2));

            // Calcular data prevista se não foi informada
            if (!p.data_prevista) {
              const daysToAdd = settlementConfig.days_to_receive || 0;
              const baseDate = p.data ? new Date(p.data) : new Date();
              dataPrevista = new Date(baseDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000)
                .toISOString()
                .slice(0, 10);
            }
          } else if (p.status === 'pendente' && !p.data_prevista) {
            // Pendente sem data prevista: usar data do pagamento ou hoje
            dataPrevista = p.data || new Date().toISOString().slice(0, 10);
          }

          return {
            transaction_id: transactionResult.id,
            tenant_id: tenantId,
            bank_account_id: p.bank_account_id,
            payment_method_id: p.payment_method_id,
            valor: p.valor,
            net_amount: Number(netAmount.toFixed(2)),
            fee_amount: Number((feeAmount ?? 0).toFixed(2)),
            status: p.status,
            data: p.data || null,
            descricao: p.descricao || null,
            data_prevista: dataPrevista,
            settlement_config_id: settlementConfig?.id || null,
            created_at: p.created_at ? new Date(p.created_at).toISOString() : undefined,
            confirmed_at: p.confirmed_at ? new Date(p.confirmed_at).toISOString() : undefined,
          };
        });
        const { error: paysErr } = await supabase.from("transaction_payments").insert(paymentsToInsert as any);
        if (paysErr) throw paysErr;
      }

      return transactionResult;
    },
    onSuccess: () => {
      toast.success(transaction?.id ? "Venda atualizada com sucesso!" : "Venda registrada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["caixa-transactions"] });
      onOpenChange(false);
      form.reset();
      setItems([]);
      setPayments([]);
    },
    onError: (error: Error) => {
      toast.error("Erro ao registrar venda: " + error.message);
    },
  });

  const onSubmit = (data: SaleFormData) => {
    if (items.length === 0) {
      toast.error("Adicione pelo menos um item à venda");
      return;
    }
    if (payments.length === 0) {
      toast.error("Registre pelo menos um pagamento (pode ser pendente). Recomenda-se informar uma data prevista de pagamento.");
      return;
    }
    createSaleMutation.mutate(data);
  };

  const valorTotalItens = items.reduce((sum, item) => sum + item.total, 0);
  const desconto = Number(form.watch("desconto") || 0);
  const valorLiquido = valorTotalItens - desconto;
  const valorPago = payments.filter(p => p.status === "confirmado").reduce((sum, p) => sum + p.valor, 0);
  const valorPendente = valorLiquido - valorPago;

  const dismissProductSelect = () => {
    // Fecha qualquer select de produto aberto e remove o blur
    saleItemsRef.current?.closeProductSelect?.();
  };

  // Listener global para garantir remoção do blur em qualquer clique fora/ESC
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      // Ignora cliques dentro do conteúdo do Popover (Radix)
      const target = e.target as HTMLElement | null;
      if (target && (target.closest('[data-radix-popper-content-wrapper]') || target.closest('[data-radix-popover-content]'))) {
        return;
      }
      // Se algum select de produto estiver aberto, fecha
      if (productSelectOpen) dismissProductSelect();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && productSelectOpen) dismissProductSelect();
    };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, productSelectOpen]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden">
          <div className="flex max-h-[90vh] flex-col relative">
            <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
              <DialogTitle>{readOnly ? "Visualizando Venda" : (transaction?.id ? "Editar Venda" : "Nova Venda")}</DialogTitle>
              {transaction?.id && (
                <div className="absolute right-12 top-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setShowPrintPreview(true)}
                    title="Visualizar para impressão"
                  >
                    <Printer className="h-4 w-4" />
                    <span>Imprimir</span>
                  </Button>
                </div>
              )}
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-6 py-4 relative">
              <Form {...form}>
                <form id="sale-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-4">
                <FormField
                  control={form.control}
                  name="client_id"
                  render={({ field }) => (
                    <FormItem>
                      {readOnly && (
                        <FormLabel>
                          {field.value ? (
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setClientProfile({ id: field.value, name: (clients ?? []).find(c => c.id === field.value)?.nome || 'cliente' });
                                    }}
                                    className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                  >
                                    <span>Cliente *</span>
                                    <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="whitespace-pre-line z-[9999]" side="bottom" sideOffset={8} align="start">
                                  Ver ficha completa e relatório{'\n'}do cliente <strong>{(clients ?? []).find(c => c.id === field.value)?.nome || 'cliente'}</strong>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span>Cliente *</span>
                          )}
                        </FormLabel>
                      )}
                      <div className={`flex gap-2 pt-1 ${clientSelectOpen ? "relative z-[5000]" : ""}`}>
                        <FormControl className="w-full">
                          {readOnly ? (
                            <div className="h-10 w-full rounded-md border border-input bg-muted px-3 text-sm flex items-center">
                              {(clients ?? []).find(c => c.id === field.value)?.nome || ""}
                            </div>
                          ) : (
                            <SearchableSelect
                              label={
                                field.value ? (
                                  <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setClientProfile({ id: field.value, name: (clients ?? []).find(c => c.id === field.value)?.nome || 'cliente' });
                                          }}
                                          className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                        >
                                          <span>Cliente *</span>
                                          <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent className="whitespace-pre-line z-[9999]" side="bottom" sideOffset={8} align="start">
                                        Ver ficha completa e relatório{'\n'}do cliente <strong>{(clients ?? []).find(c => c.id === field.value)?.nome || 'cliente'}</strong>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <span>Cliente *</span>
                                )
                              }
                              open={clientSelectOpen}
                              onOpenChange={setClientSelectOpen}
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                setClientSelectOpen(false);
                                requestAnimationFrame(() => {
                                  saleItemsRef.current?.openNewRowProductSelect();
                                });
                              }}
                              options={(clients ?? []).map(c => ({ value: c.id, label: c.nome }))}
                              placeholder="Selecione o cliente"
                              renderAddModal={({ open, onOpenChange, onCreated }) => (
                                <QuickAddClientModal
                                  open={open}
                                  onOpenChange={onOpenChange}
                                  onClientCreated={async (clientId) => {
                                    await queryClient.invalidateQueries({ queryKey: ["clients"] });
                                    onCreated(clientId);
                                  }}
                                />
                              )}
                            />
                          )}
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="vendedor_id"
                  render={({ field }) => (
                    <FormItem>
                      {readOnly && (
                        <FormLabel>
                          {field.value ? (
                            <TooltipProvider delayDuration={300}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setUserProfile({ id: field.value, name: (sellers ?? []).find(s => s.id === field.value)?.nome_completo || (field.value === currentUser?.id ? (currentUser as any)?.user_metadata?.nome_completo : 'usuário') });
                                    }}
                                    className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                  >
                                    <span>Vendedor *</span>
                                    <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="whitespace-pre-line z-[9999]" side="bottom" sideOffset={8} align="start">
                                  Ver ficha completa e relatórios{'\n'}do usuário <strong>{(sellers ?? []).find(s => s.id === field.value)?.nome_completo || (field.value === currentUser?.id ? (currentUser as any)?.user_metadata?.nome_completo : 'usuário')}</strong>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span>Vendedor *</span>
                          )}
                        </FormLabel>
                      )}
                      <div className={`${sellerSelectOpen ? "relative z-[5000]" : ""} pt-1`}>
                        <FormControl>
                          {readOnly ? (
                            <div className="h-10 w-full rounded-md border border-input bg-muted px-3 text-sm flex items-center">
                              {(sellers ?? []).find(s => s.id === field.value)?.nome_completo || (field.value === currentUser?.id ? (currentUser as any)?.user_metadata?.nome_completo : "")}
                            </div>
                          ) : (
                            <SearchableSelect
                              label={
                                field.value ? (
                                  <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setUserProfile({ id: field.value, name: (sellers ?? []).find(s => s.id === field.value)?.nome_completo || (field.value === currentUser?.id ? (currentUser as any)?.user_metadata?.nome_completo : 'usuário') });
                                          }}
                                          className="flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer group"
                                        >
                                          <span>Vendedor *</span>
                                          <FileText className="h-4 w-4 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent className="whitespace-pre-line z-[9999]" side="bottom" sideOffset={8} align="start">
                                        Ver ficha completa e relatórios{'\n'}do usuário <strong>{(sellers ?? []).find(s => s.id === field.value)?.nome_completo || (field.value === currentUser?.id ? (currentUser as any)?.user_metadata?.nome_completo : 'usuário')}</strong>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <span>Vendedor *</span>
                                )
                              }
                              open={sellerSelectOpen}
                              onOpenChange={setSellerSelectOpen}
                              value={field.value}
                              onChange={field.onChange}
                              options={(sellers ?? []).map(s => ({ value: s.id, label: s.nome_completo }))}
                              placeholder="Selecione o vendedor"
                              valueLabel={(sellers ?? []).find(s => s.id === field.value)?.nome_completo ||
                                (field.value === currentUser?.id ? (currentUser as any)?.user_metadata?.nome_completo : undefined)}
                              renderAddModal={({ open, onOpenChange, onCreated }) => (
                                <UserModal
                                  isOpen={open}
                                  onClose={() => onOpenChange(false)}
                                  user={null}
                                  onCreated={({ id, nome_completo }) => {
                                    // Seleciona imediatamente o usuário criado
                                    field.onChange(id);
                                    // Atualiza lista de vendedores para incluir o novo
                                    queryClient.invalidateQueries({ queryKey: ["sellers", tenantId] });
                                    onCreated(id);
                                  }}
                                />
                              )}
                            />
                          )}
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="desconto"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Desconto</FormLabel>
                      <FormControl>
                        <MoneyInput
                          value={field.value ?? 0}
                          onValueChange={(value) => !readOnly && field.onChange(value)}
                          onBlur={field.onBlur}
                          placeholder="0,00"
                          disabled={readOnly}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="data_entrega"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Entrega</FormLabel>
                      <FormControl>
                        <DateField {...field} disabled={readOnly} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className={productSelectOpen ? "relative z-[52]" : undefined}>
                <SaleItemsCards
                  ref={saleItemsRef}
                  items={items}
                  onItemsChange={readOnly ? () => {} : setItems}
                  onProductSelectOpenChange={setProductSelectOpen}
                  readOnly={readOnly}
                />
              </div>

              {canProceed && (
                <>
                  {/* Onboarding de Pagamentos */}
                  {showOnboarding && !readOnly && (
                    <div className="mt-6" onMouseDownCapture={dismissProductSelect} onFocusCapture={dismissProductSelect}>
                      <PaymentOnboarding onComplete={() => setShowOnboarding(false)} />
                    </div>
                  )}

                  {/* Blocos exibidos apenas após concluir o onboarding */}
                  {!showOnboarding && (
                    <>
                      <div className="grid gap-4 md:grid-cols-2" onMouseDownCapture={dismissProductSelect} onFocusCapture={dismissProductSelect}>
                        <div className="space-y-4 mt-0">
                          <div className="bg-gray-200 dark:bg-muted px-4 py-2 rounded-lg">
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                              <div className="flex flex-col items-start text-md">
                                <div className="text-left">
                                  <span className="text-muted-foreground inline-block mr-2">
                                    Subtotal: 
                                  </span>
                                  <span className="">
                                    {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorTotalItens)}
                                  </span>
                                </div>
                                {desconto > 0 && <div>
                                  <span className="text-muted-foreground inline-block mr-2">
                                    Desconto Geral: 
                                  </span>
                                  <span className="font-black text-orange-300 inline-block">
                                    - {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(desconto)}
                                  </span>
                                </div>}
                              </div>
                              <div className="flex items-middle gap-2 text-xl font-bold ml-auto">
                                <span>Total:</span>
                                <span>
                                  {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valorLiquido)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <PaymentsList
                            payments={payments}
                            onPaymentsChange={setPayments}
                            valorTotal={valorLiquido}
                            valorPago={valorPago}
                            valorPendente={valorPendente}
                            showSummary={true}
                            showDetails={false}
                          />
                        </div>

                <div 
                    className="-mt-1">
                          <FormField
                            control={form.control}
                            name="descricao"
                            render={({ field }) => (
                              <FormItem className="h-full">
                                <FormLabel>Observações adicionais do pedido:</FormLabel>
                                <FormControl>
                                  {readOnly ? (
                                    <div
                                      className={`${desconto > 0 ? "min-h-[125px]" : "min-h-[105px]"} w-full rounded-md border border-input bg-muted px-3 py-2 text-sm ${!field.value ? "text-muted-foreground" : ""}`}
                                    >
                                      {field.value && field.value.trim().length > 0 ? field.value : "Nenhuma observação"}
                                    </div>
                                  ) : (
                                    <Textarea {...field} rows={4} className={desconto > 0?"min-h-[125px]":"min-h-[105px]"} />
                                  )}
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      {/* Detalhes de Pagamentos em largura total */}
                      <div onClick={dismissProductSelect} onFocusCapture={dismissProductSelect}>
                      <PaymentsList
                        payments={payments}
                        onPaymentsChange={readOnly ? () => {} : setPayments}
                        valorTotal={valorLiquido}
                        valorPago={valorPago}
                        valorPendente={valorPendente}
                        showSummary={false}
                        showDetails={true}
                        readOnly={readOnly}
                      />
                      </div>
                    </>
                  )}
                </>
              )}

              </form>
            </Form>
            </div>

            <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {readOnly ? "Fechar" : "Cancelar"}
              </Button>
              {!readOnly && (
                <Button
                  type="submit"
                  form="sale-form"
                  disabled={createSaleMutation.isPending || payments.length === 0}
                  onClick={(e) => {
                    if (payments.length === 0) {
                      e.preventDefault();
                      toast.error("É necessário registrar pelo menos um pagamento. Você pode registrar como pendente e informar uma data prevista.");
                    }
                  }}
                >
                  {createSaleMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {transaction?.id ? "Salvar Alterações" : "Registrar Venda"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QuickAddClientModal agora é injetado via renderAddModal do SearchableSelect */}
      
      {clientProfile && (() => {
        const c = (clients || []).find((x: any) => x.id === clientProfile.id);
        const photo = c?.foto_url || null;
        const infoItems = [
          c?.created_at ? { value: `Cliente desde ${new Intl.DateTimeFormat('pt-BR').format(new Date(c.created_at))}` } : null,
          c?.telefone ? { value: formatPhone(c.telefone as string) } : null,
          c?.email ? { value: c.email } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!clientProfile}
            onOpenChange={() => setClientProfile(null)}
            imageUrl={photo || null}
            entityTypeLabel={"CLIENTE"}
            entityName={clientProfile.name}
            infoItems={infoItems}
            showPeriodControls
            initialFrom={startOfDay(subDays(new Date(), 6))}
            initialTo={endOfDay(new Date())}
            renderContent={({ periodRange, setLoading }) => (
              <ClientDetails id={clientProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}

      {userProfile && (() => {
        const u = (sellers || []).find((x: any) => x.id === userProfile.id) || (currentUser && currentUser.id === userProfile.id ? currentUser : null);
        const infoItems = [
          (u as any)?.created_at ? { value: `Usuário desde ${new Intl.DateTimeFormat('pt-BR').format(new Date((u as any).created_at))}` } : null,
          (u as any)?.email ? { value: (u as any).email } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!userProfile}
            onOpenChange={() => setUserProfile(null)}
            imageUrl={null}
            entityTypeLabel={"USUÁRIO"}
            entityName={userProfile.name}
            infoItems={infoItems}
            showPeriodControls
            initialFrom={startOfDay(subDays(new Date(), 6))}
            initialTo={endOfDay(new Date())}
            renderContent={({ periodRange, setLoading }) => (
              <UserDetails id={userProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}
      {transaction?.id && (
        <PrintPreviewModal
          open={showPrintPreview}
          onOpenChange={setShowPrintPreview}
          transaction={transaction}
          items={items}
          payments={payments}
          client={clients?.find((c: any) => c.id === form.watch("client_id"))}
          seller={sellers?.find((s: any) => s.id === form.watch("vendedor_id"))}
          tenant={{ name: tenantName || tenantData?.name || "", email: tenantData?.email || null }}
        />
      )}
    </>
  );
}
